import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [token, setToken] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        const t = params.get("token") || "";
        setToken(t);
    }, [params]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            toast.error("Les mots de passe ne correspondent pas");
            return;
        }
        if (password.length < 8) {
            toast.error("Au moins 8 caractères");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/password-reset/apply", {
                token,
                new_password: password,
            });
            setDone(true);
            setTimeout(() => navigate("/login"), 2500);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Lien invalide ou expiré");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen">
            <PublicHeader />
            <section className="pt-32 pb-24 px-6 sm:px-8">
                <div className="max-w-md mx-auto tk-card p-8 sm:p-10">
                    {done ? (
                        <div className="text-center" data-testid="reset-success">
                            <CheckCircle2 className="w-12 h-12 text-[#34C759] mx-auto mb-4" />
                            <h1 className="text-2xl font-semibold tracking-tight text-[#1D1D1F]">
                                Mot de passe mis à jour
                            </h1>
                            <p className="mt-3 text-sm text-[#86868B]">
                                Redirection vers la connexion…
                            </p>
                        </div>
                    ) : !token ? (
                        <div className="text-center">
                            <AlertCircle className="w-12 h-12 text-[#FF9F0A] mx-auto mb-4" />
                            <h1 className="text-2xl font-semibold tracking-tight text-[#1D1D1F]">
                                Lien invalide
                            </h1>
                            <p className="mt-3 text-sm text-[#86868B]">
                                Ce lien ne contient pas de token. Demandez-en un nouveau.
                            </p>
                            <Link to="/forgot-password" className="tk-btn tk-btn-primary mt-6 inline-flex">
                                Nouveau lien
                            </Link>
                        </div>
                    ) : (
                        <>
                            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1D1D1F]">
                                Nouveau mot de passe
                            </h1>
                            <p className="mt-2 text-sm text-[#86868B]">8 caractères minimum.</p>
                            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                                <Field label="Nouveau mot de passe" icon={Lock}>
                                    <input
                                        type="password"
                                        required
                                        minLength={8}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="tk-input"
                                        data-testid="reset-password"
                                    />
                                </Field>
                                <Field label="Confirmation" icon={Lock}>
                                    <input
                                        type="password"
                                        required
                                        minLength={8}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className="tk-input"
                                        data-testid="reset-confirm"
                                    />
                                </Field>
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="tk-btn tk-btn-primary w-full justify-center"
                                    data-testid="reset-submit"
                                >
                                    {busy ? "Mise à jour…" : "Mettre à jour"}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}

function Field({ label, icon: Icon, children }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#86868B] mb-2 flex items-center gap-2">
                <Icon className="w-3.5 h-3.5" /> {label}
            </label>
            {children}
        </div>
    );
}
